import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from '../../../../components/ui/Icon/Icon'

import Loader from '../../../../assets/img/loader.gif'
import MoreHeaderParticles from '../../../../assets/img/more-header-particles.svg'

import * as WatchApiServices from '../../../../services/api/requests/Watch'

import dateFormat, { timeFormat } from '../../../../services/data/dateFormat'
import { MAIN } from '../../../../services/data/profilePages.json'

import RightSideBarHeader from '../../../../assets/img/right-sidebar-header.svg'

import TicketsPageDetails from '../TicketsPageDetails/TicketsPageDetails'

const MainPage = styled.div`
  display: ${props => (props.show ? 'block' : 'none')};
  overflow: auto;
  height: 100%;

  &::before {
    content: ' ';
    display: block;
    height: 244px;
    background-color: var(--color-primary);
    background: url('${RightSideBarHeader}'), url('${MoreHeaderParticles}'), linear-gradient(var(--color-primary), var(--color-primary));
    background-position: bottom, right top;
    background-repeat: no-repeat;
    background-size: 100%, auto;
  }
`

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-height: 87px;
  font-size: 0.7rem;
  z-index: 100;

  ${props =>
    props.hasScrolled &&
    css`
    background: url('${MoreHeaderParticles}'), linear-gradient(var(--color-primary), var(--color-primary));
    background-repeat: no-repeat;
    background-position: right top;
  `}
`

const ReturnIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const Title = styled.h1`
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-right: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;
`

const Content = styled.div`
  padding: 1rem;
`

const Ticket = styled.div`
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`

const TicketList = styled.div`
  ${Ticket} {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const ListDescription = styled.p`
  text-align: center;
`

const BlankContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

const TicketHeader = styled.div`
  position: relative;
  padding: 1rem;
  display: flex;
  border-bottom: 1.5px dashed var(--color-fg-lighter);

  &::before,
  &::after {
    content: ' ';
    bottom: -0.7rem;
    position: absolute;
    height: 20px;
    width: 10px;
    border: 1px solid #e5e5e5;
    background: #ffffff;
  }

  &::before {
    left: -1px;
    border-radius: 0 16px 16px 0;
    border-left-color: #ffffff;
  }

  &::after {
    right: -1px;
    border-radius: 16px 0 0 16px;
    border-right-color: #ffffff;
  }
`

const MovieImage = styled.div.attrs(props => ({
  style: { backgroundImage: `url('${props.image}')` },
}))`
  background-size: cover;
  background-position: center;
  border-radius: 5px;
  height: 3.7rem;
  width: 3.7rem;
  margin-right: 1rem;
`

const MovieTitleWrapper = styled.div`
  flex: 1;
`

const MovieTitle = styled.h3`
  font-size: 1rem;
  letter-spacing: 0.5px;
  font-size: var(--font-weight-bold);
  margin: 0;
`

const Theater = styled.p`
  color: #9b9b9b;
  font-size: 0.8rem;
  letter-spacing: 0.5;
  margin: 0;
`

const TicketDetails = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Date = styled.div`
  background: #eb7b88;
  flex: 0 1 100px;
  text-align: center;
  border-radius: 0.3rem;
  font-size: 0.54rem;
  line-height: 1rem;
  padding: 0.2rem 0.5rem;
  color: #fff;
`

const TicketDetailsSection = styled.div`
  font-size: 0.8rem;
  line-height: 0.9rem;
`

const SectionTitle = styled.p`
  color: var(--color-fg-light);
  font-size: 0.7rem;
  line-height: 0.7rem;
  margin: 0;
`

const StyledLoader = styled.img.attrs({ src: Loader })`
  max-height: 30px;
`

class TicketsPage extends React.Component {
  state = {
    loadingStatus: null,
    tickets: [],
    selectedTicket: null,
    hasScrolled: false,
  }

  currentPage = React.createRef()

  componentDidMount() {
    this.loadTickets()
    this.currentPage.current.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    this.currentPage.current.removeEventListener('scroll', this.handleScroll)
  }

  showDetailPage = async data => {
    this.setState({
      selectedTicket: data,
      detailHasScrolled: false,
    })
  }

  closeDetailPage = () => {
    this.setState({
      selectedTicket: null,
    })
  }

  loadTickets = async () => {
    this.setState({
      loadingStatus: 0,
    })

    const { accessToken } = this.props

    try {
      const ticketResponse = await WatchApiServices.getAllTickets(accessToken)

      this.setState({
        loadingStatus: 1,
        tickets: ticketResponse.data,
      })
    } catch (err) {
      this.setState({
        tickets: [],
        loadingStatus: 4,
      })
    }
  }

  handleScroll = event => {
    const { hasScrolled: hasScrolledState } = this.state
    const { scrollTop } = event.target
    const hasScrolled = scrollTop > 0

    if (hasScrolled !== hasScrolledState) {
      this.setState({
        hasScrolled,
      })
    }
  }

  render() {
    const { loadingStatus, hasScrolled, tickets, selectedTicket } = this.state
    const { setProfileModalPage } = this.props

    return (
      <>
        <MainPage ref={this.currentPage} show={!selectedTicket}>
          <Header hasScrolled={hasScrolled}>
            <ReturnIcon onClick={() => setProfileModalPage(MAIN)}>
              <Icon name="slide-left" />
            </ReturnIcon>
            <Title>My Movie Tickets</Title>
          </Header>
          <Content>
            {loadingStatus === 0 && (
              <BlankContainer>
                <StyledLoader />
              </BlankContainer>
            )}
            {loadingStatus === 1 && (
              <>
                <ListDescription>
                  You currently have {tickets.length} movie{' '}
                  {tickets.length !== 1 ? 'tickets' : 'ticket'}.
                </ListDescription>
                <ListDescription>
                  Skip the lines. Present your movie ticket directly at the
                  assigned cinema and prepare for an enjoyable movie experience.
                </ListDescription>
                <ListDescription>
                  Conveniently book more movie tickets by clicking the Cinemas
                  tab.
                </ListDescription>
              </>
            )}
            <TicketList>
              {loadingStatus === 1 &&
                tickets.map(props => (
                  <Ticket
                    key={props.transactionId}
                    onClick={() => this.showDetailPage(props)}
                  >
                    <TicketHeader>
                      <MovieImage image={props.movie.imageUrl} />
                      <MovieTitleWrapper>
                        <MovieTitle>{props.movie.title}</MovieTitle>
                        <Theater>@ {props.theater}</Theater>
                      </MovieTitleWrapper>
                    </TicketHeader>
                    <TicketDetails>
                      <Date>{dateFormat(props.reserveAt)}</Date>
                      <TicketDetailsSection>
                        <SectionTitle>Time</SectionTitle>
                        {timeFormat(props.reserveAt)}
                      </TicketDetailsSection>
                      <TicketDetailsSection>
                        <SectionTitle>Theater</SectionTitle>
                        {props.cinema}
                      </TicketDetailsSection>
                      <TicketDetailsSection>
                        <SectionTitle>Seats</SectionTitle>
                        {props.seats.length} seats
                      </TicketDetailsSection>
                    </TicketDetails>
                  </Ticket>
                ))}
            </TicketList>
            {loadingStatus === 1 && tickets.length === 0 && (
              <BlankContainer>No Records Found</BlankContainer>
            )}
          </Content>
        </MainPage>
        {!!selectedTicket && (
          <TicketsPageDetails
            data={selectedTicket}
            onCloseDetailPage={this.closeDetailPage}
          />
        )}
      </>
    )
  }
}

TicketsPage.defaultProps = {
  messages: [],
}

TicketsPage.propTypes = {
  messages: PropTypes.array,
}

export default TicketsPage
